import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Line, LineChart, XAxis, YAxis } from "recharts";

import { UserAvatar } from "@/components/common/UserAvatar";
import { DialogNotice } from "@/components/common/ActionDialog";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import {
  fetchPerformanceAd,
  resumePerformanceAd,
  type AdDetailView,
  type Breakdown,
} from "@/services/metaPerformance.service";
import { formatDate } from "@/utils/formatters";
import { cn } from "@/lib/utils";

import { errorMessage } from "../../tiktok/tiktokFormat";
import { BucketText, EventOutcome, LaunchedMark, MixedMoney } from "./bits";
import { METRIC_LABEL, fmtCross, fmtDelta, fmtInt, fmtMetricValue, fmtMoney, fmtPct, fmtRoas, shortDay } from "./format";

type BdTab = "none" | Breakdown;
const BD_TABS: Array<{ value: BdTab; label: string }> = [
  { value: "none", label: "Totals" },
  { value: "placement", label: "Placement" },
  { value: "country", label: "Country" },
  { value: "age_gender", label: "Age · gender" },
];

/**
 * One ad, end to end: the creator and content behind it, how the video
 * held attention, what Meta and Reveshare each attribute to it, what the
 * content cost, and any rule that touched it.
 */
export function AdDetailSheet({ metaAdId, days, onClose }: { metaAdId: string | null; days: number; onClose: () => void }) {
  const [bd, setBd] = useState<BdTab>("none");
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["meta-performance", "ad", metaAdId, days, bd],
    queryFn: () => fetchPerformanceAd(metaAdId!, days, bd === "none" ? null : bd),
    enabled: !!metaAdId,
    placeholderData: (prev) => prev,
  });
  const resume = useMutation({
    mutationFn: () => resumePerformanceAd(metaAdId!),
    onSuccess: () => {
      toast.success("Ad is live again on Meta");
      void queryClient.invalidateQueries({ queryKey: ["meta-performance"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const d = q.data;
  const cur = d?.currency.account ?? "";
  const money = (v: number) => fmtMoney(v, cur);

  return (
    <Sheet open={!!metaAdId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-2xl">
        <SheetHeader className="pr-6">
          <SheetTitle className="text-[15px]">{d?.ad.name ?? "Ad"}</SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {d && (
              <>
                <BucketText bucket={d.ad.bucket} roas={fmtRoas(d.ad.meta.roas)} />
                <LaunchedMark on={d.ad.reveshareLaunched} />
                {d.ad.startTime && <span>Since {formatDate(d.ad.startTime)}</span>}
                <span>Last {days} days</span>
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {q.isLoading || !d ? (
          <div className="mt-6 space-y-3 animate-pulse">
            <div className="h-20 rounded-md bg-muted" />
            <div className="h-40 rounded-md bg-muted" />
            <div className="h-40 rounded-md bg-muted" />
          </div>
        ) : (
          <div className={cn("mt-5 space-y-6", q.isFetching && "opacity-70")}>
            {d.ad.launched?.pausedByReveshareAt && (
              <DialogNotice tone="warning">
                <span className="flex flex-wrap items-center justify-between gap-2">
                  <span>Paused by a rule on {formatDate(d.ad.launched.pausedByReveshareAt)}. It stays paused until you resume it.</span>
                  <Button size="sm" variant="outline" onClick={() => resume.mutate()} disabled={resume.isPending}>
                    {resume.isPending ? "Resuming" : "Resume on Meta"}
                  </Button>
                </span>
              </DialogNotice>
            )}

            {d.ad.launched && <CreatorBlock d={d} />}

            <Section title="This period vs the one before">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                <Stat label="Spend" value={money(d.ad.meta.spend)} delta={d.delta.spend} days={days} />
                <Stat label="Revenue (Meta)" value={money(d.ad.meta.purchaseValue)} delta={d.delta.purchaseValue} days={days} />
                <Stat label="Purchases" value={fmtInt(d.ad.meta.purchases)} delta={d.delta.purchases} days={days} />
                <Stat label="ROAS" value={fmtRoas(d.ad.meta.roas)} delta={d.delta.roas} days={days} />
                <Stat label="CTR" value={fmtPct(d.ad.meta.ctr)} delta={d.delta.ctr} days={days} />
                <Stat label="CPC" value={d.ad.meta.cpc != null ? money(d.ad.meta.cpc) : "—"} delta={d.delta.cpc} days={days} />
                <Stat label="Cost per purchase" value={d.ad.meta.cpa != null ? money(d.ad.meta.cpa) : "—"} delta={d.delta.cpa} days={days} />
                <Stat label="Frequency" value={d.ad.meta.frequency != null ? d.ad.meta.frequency.toFixed(2) : "—"} delta={d.delta.frequency} days={days} />
              </dl>
            </Section>

            <Section title="Spend vs revenue, daily">
              {d.series.some((p) => p.spend > 0 || p.purchaseValue > 0) ? (
                <ChartContainer
                  config={{ spend: { label: "Spend", color: "var(--chart-1)" }, purchaseValue: { label: "Revenue (Meta)", color: "var(--chart-3)" } }}
                  className="h-44 w-full"
                >
                  <LineChart data={d.series.map((p) => ({ ...p, label: shortDay(p.date) }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} width={52} tickFormatter={(v: number) => fmtMoney(v, cur, true)} />
                    <ChartTooltip content={<ChartTooltipContent formatter={(v, name) => <span className="tabular-nums"><span className="text-muted-foreground">{name === "spend" ? "Spend" : "Revenue"}</span> {money(Number(v) || 0)}</span>} />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Line type="monotone" dataKey="spend" stroke="var(--color-spend)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="purchaseValue" stroke="var(--color-purchaseValue)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  </LineChart>
                </ChartContainer>
              ) : (
                <p className="text-sm text-muted-foreground">No delivery in this period.</p>
              )}
            </Section>

            <Section title="How the video held attention" hint="Each step as a share of impressions. Hook rate is the 3-second step.">
              <Funnel d={d} />
            </Section>

            <Section title="Attribution: Meta vs Reveshare" hint="Meta counts pixel purchases it saw; Reveshare counts orders the storefront pixel tied to this ad. They will not match exactly.">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-border p-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Meta</div>
                  <dl className="mt-2 space-y-1 text-sm">
                    <Row k="Purchases" v={fmtInt(d.ad.meta.purchases)} />
                    <Row k="Purchase value" v={money(d.ad.meta.purchaseValue)} />
                    <Row k="ROAS" v={fmtRoas(d.ad.meta.roas)} />
                  </dl>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Reveshare</div>
                  <dl className="mt-2 space-y-1 text-sm">
                    <Row k="Orders" v={fmtInt(d.ad.reveshare.orders)} />
                    <Row k="Revenue" v={<MixedMoney m={d.ad.reveshare.revenue} fallbackCurrency={cur} />} />
                    <Row k="Commission owed" v={<MixedMoney m={d.ad.reveshare.commission} fallbackCurrency={cur} />} />
                    <Row k="Net ROAS" v={fmtCross(d.ad.reveshare.netRoas)} />
                    {d.creatorCost && <Row k="Content cost (fee + commission)" v={<MixedMoney m={d.creatorCost} fallbackCurrency={cur} />} />}
                    {d.trueRoas && <Row k="True ROAS (after content cost)" v={fmtCross(d.trueRoas)} />}
                  </dl>
                </div>
              </div>
              {(d.ad.reveshare.netRoas.reason === "CURRENCY_CONFLICT" || d.currency.conflict) && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">Ads are billed in {d.currency.account} and the store sells in {d.currency.store ?? "another currency"}. Nothing is converted, so figures across the two are left blank.</p>
              )}
            </Section>

            <Section title="Breakdown">
              <UnderlineTabs tabs={BD_TABS} value={bd} onValueChange={setBd} accent="brand" />
              {bd !== "none" && (
                d.breakdown && d.breakdown.rows.length > 0 ? (
                  <table className="mt-3 w-full text-sm">
                    <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr><th className="py-1.5 font-medium">{BD_TABS.find((t) => t.value === bd)?.label}</th><th className="py-1.5 text-right font-medium">Spend</th><th className="py-1.5 text-right font-medium">CTR</th><th className="py-1.5 text-right font-medium">Purchases</th><th className="py-1.5 text-right font-medium">ROAS</th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {d.breakdown.rows.map((r) => (
                        <tr key={r.label}>
                          <td className="py-1.5 text-foreground">{r.label}</td>
                          <td className="py-1.5 text-right tabular-nums">{money(r.meta.spend)}</td>
                          <td className="py-1.5 text-right tabular-nums">{fmtPct(r.meta.ctr)}</td>
                          <td className="py-1.5 text-right tabular-nums">{fmtInt(r.meta.purchases)}</td>
                          <td className="py-1.5 text-right tabular-nums">{fmtRoas(r.meta.roas)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">{q.isFetching ? "Loading breakdown" : "No breakdown rows for this period."}</p>
                )
              )}
            </Section>

            {d.events.length > 0 && (
              <Section title="Rule history">
                <ul className="divide-y divide-border text-sm">
                  {d.events.map((e) => (
                    <li key={e.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="text-foreground">{METRIC_LABEL[e.metric]}: {fmtMetricValue(e.metric, e.observed)} vs {fmtMetricValue(e.metric, e.threshold)}</div>
                        <div className="text-xs text-muted-foreground">{formatDate(e.createdAt)}{e.resolvedAt ? ` · cleared ${formatDate(e.resolvedAt)}` : " · open"}</div>
                      </div>
                      <EventOutcome outcome={e.outcome} />
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CreatorBlock({ d }: { d: AdDetailView }) {
  const l = d.ad.launched!;
  const c = l.creator;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border p-3">
      {l.content.thumbnailUrl ? (
        <img src={l.content.thumbnailUrl} alt="" className="size-16 shrink-0 rounded-md object-cover" />
      ) : c ? (
        <UserAvatar src={c.profileImage} name={`${c.firstName} ${c.lastName}`} className="size-16 shrink-0" />
      ) : null}
      <div className="min-w-0 flex-1 text-sm">
        <div className="truncate font-medium text-foreground">{l.content.title ?? "Untitled content"}</div>
        {c && (
          <div className="truncate text-muted-foreground">
            By <Link to={`/brand/affiliates/${c.id}`} className="text-foreground underline-offset-4 hover:underline">{c.firstName} {c.lastName}</Link>
            {c.username ? ` · @${c.username}` : ""}
          </div>
        )}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <Link to={`/brand/content-programs/${l.program.id}`} className="underline-offset-4 hover:underline">{l.program.title}</Link>
          {l.adRequest && (
            <Link to="/brand/tiktok?tab=ad-requests" className="underline-offset-4 hover:underline">
              TikTok request · {l.adRequest.flatFee ? `${fmtMoney(l.adRequest.flatFee, d.currency.store ?? d.currency.account)} fee` : "no fee"}
              {l.adRequest.commissionRate != null ? ` · ${l.adRequest.commissionRate}%` : ""}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function Funnel({ d }: { d: AdDetailView }) {
  const steps = d.funnel.filter((s) => !(s.key.startsWith("p") && s.value === 0 && d.funnel.find((x) => x.key === "video3s")?.value === 0));
  if (d.ad.meta.impressions === 0) return <p className="text-sm text-muted-foreground">No impressions yet.</p>;
  return (
    <ol className="space-y-1.5">
      {steps.map((s) => (
        <li key={s.key} className="grid grid-cols-[9rem_1fr_auto] items-center gap-3 text-sm">
          <span className="truncate text-muted-foreground">{s.label}</span>
          <span className="h-2 overflow-hidden rounded-sm bg-muted">
            <span className="block h-full rounded-sm bg-brand-600" style={{ width: `${Math.max(1, (s.ofImpressions ?? 0) * 100)}%` }} />
          </span>
          <span className="w-28 text-right tabular-nums text-foreground">
            {fmtInt(s.value)} <span className="text-xs text-muted-foreground">{s.ofImpressions != null ? `${(s.ofImpressions * 100).toFixed(1)}%` : ""}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>
      {hint && <p className="mb-2 text-xs text-muted-foreground">{hint}</p>}
      <div className={cn(!hint && "mt-2")}>{children}</div>
    </section>
  );
}

function Stat({ label, value, delta, days }: { label: string; value: string; delta: number | null | undefined; days: number }) {
  const c = fmtDelta(delta, days);
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="truncate text-lg font-semibold tabular-nums text-foreground">{value}</dd>
      <dd className={cn("truncate text-[11px]", c.direction === "up" ? "text-emerald-700 dark:text-emerald-400" : c.direction === "down" ? "text-red-700 dark:text-red-400" : "text-muted-foreground")}>{c.text}</dd>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-right tabular-nums text-foreground">{v}</dd>
    </div>
  );
}

